import { Box, Typography, useTheme } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { tokens } from '../../../global/theme/tokens';
import { secondsToHours, absenceSeconds } from './workTimeReportUtils';
import AbsencePopper from './AbsencePopper';

const StatCard = ({ label, value, accentColor, onClick, anchorRef, badge }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const clickable = Boolean(onClick);

  return (
    <Box
      ref={anchorRef}
      onClick={onClick}
      className="flex-1 min-w-[120px] p-3 rounded-lg backdrop-blur-lg flex flex-col gap-1 transition-opacity"
      sx={{
        bgcolor: colors.card,
        borderLeft: accentColor ? `3px solid ${accentColor}` : 'none',
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { opacity: 0.85 } : {},
      }}
    >
      <Box className="flex items-center justify-between gap-1">
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {label}
        </Typography>
        {badge}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {value} <Typography component="span" variant="body2" sx={{ opacity: 0.6 }}>h</Typography>
      </Typography>
    </Box>
  );
};

const WorkTimeReportSummary = ({ userOverview }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const absenceAnchor = useRef(null);
  const [absenceOpen, setAbsenceOpen] = useState(false);

  return (
    <Box className="flex flex-row gap-2 flex-wrap">
      <StatCard
        label={t('Customer hours')}
        value={secondsToHours(userOverview.customerWorkedTimeInSeconds)}
        accentColor={colors.success}
      />
      <StatCard
        label={t('Internal hours')}
        value={secondsToHours(userOverview.internalWorkedTimeInSeconds)}
        accentColor={colors.info}
      />
      <StatCard
        label={t('Absence hours')}
        value={secondsToHours(absenceSeconds(userOverview))}
        accentColor={colors.warning}
        anchorRef={absenceAnchor}
        onClick={() => setAbsenceOpen((prev) => !prev)}
        badge={<InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.6 }} />}
      />
      <StatCard
        label={t('Billable hours')}
        value={secondsToHours(userOverview.billableWorkedTimeInSeconds)}
        accentColor={colors.money?.positive || colors.success}
      />

      <AbsencePopper
        open={absenceOpen}
        anchorEl={absenceAnchor.current}
        onClose={() => setAbsenceOpen(false)}
        userOverview={userOverview}
      />
    </Box>
  );
};

export default WorkTimeReportSummary;
